import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";
import { TESTIMONIALS } from "@/data/content";

export function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const t = TESTIMONIALS[idx];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 lg:py-32 bg-ivory relative overflow-hidden">
      <div className="pointer-events-none absolute top-20 right-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionHeading
          align="center"
          chapter="07"
          overline="Testimonials"
          title="Trusted by Our Patients"
          sub="What a visit to Dyntra Clinics feels like — in the words of patients."
        />
        <Reveal className="mt-14">
          <div
            data-testid="testimonial-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative rounded-[2rem] bg-white border border-pine/10 shadow-lift px-7 py-12 md:px-16 md:py-16 text-center"
          >
            <Quote className="mx-auto h-10 w-10 text-gold/50" />
            <div className="min-h-[150px] md:min-h-[120px] mt-6">
              <AnimatePresence mode="wait">
                <motion.blockquote
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="font-display text-xl md:text-2xl leading-relaxed text-pine-deep italic">“{t.text}”</p>
                </motion.blockquote>
              </AnimatePresence>
            </div>
            <div className="mt-8 flex items-center justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-gold text-gold" : "text-pine/20"}`} />
              ))}
            </div>
            <p className="mt-4 text-sm font-bold text-pine-deep">{t.name} <span className="font-medium text-slate-500">— {t.location}</span></p>
            <p className="mt-1 text-xs uppercase tracking-[0.18em] font-bold text-teal-brand">{t.service}</p>

            <div className="mt-8 flex items-center justify-center gap-4">
              <button data-testid="testimonial-prev" aria-label="Previous testimonial" onClick={() => setIdx((idx - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="rounded-full border border-pine/15 p-2.5 text-pine transition-all hover:bg-pine hover:text-ivory">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button key={i} data-testid={`testimonial-dot-${i}`} aria-label={`Go to testimonial ${i + 1}`} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-6 bg-gold" : "w-1.5 bg-pine/20"}`} />
                ))}
              </div>
              <button data-testid="testimonial-next" aria-label="Next testimonial" onClick={() => setIdx((idx + 1) % TESTIMONIALS.length)} className="rounded-full border border-pine/15 p-2.5 text-pine transition-all hover:bg-pine hover:text-ivory">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>
        <p className="mt-6 text-center text-xs text-slate-400">
          Sample testimonials shown for illustration — verified patient reviews will be published here.
        </p>
      </div>
    </section>
  );
}
